import { Controller } from "@hotwired/stimulus";
// Use 'chart.js/auto' to include scales, elements, etc.
import { Chart } from "chart.js/auto";

// Chart.js requires a date adapter for the 'time' scale.
// If your labels/data represent dates and you intend to use a time scale,
// ensure an adapter (e.g., chartjs-adapter-date-fns) is registered globally.
// This controller now explicitly REMOVES any 'x' scale config from the input JSON
// to prevent errors if the adapter is missing and type:'time' was specified.

export default class extends Controller {
  // Define instance properties
  chartInstance = null;
  currentlyHoveredIndex = null;
  originalBarDatasetCount = 0;
  lowerBoundIndex = -1;
  upperBoundIndex = -1;
  hoverLineIndex = -1;
  originalDatasetsInfo = []; // Store original colors etc.
  debouncedHoverHandler = null;
  debouncedLeaveHandler = null;
  fullDataLength = 0; // Store the length of the input data (e.g., 30)
  displayDuration = 30; // Number of data points to display
  outlierAnalysis = {}; // Store pre-computed outlier analysis from Ruby
  originalHiddenStates = []; // Store the hidden state before hover

  // --- Helper Methods (private) ---

  /**
   * Updates the stored hidden states to match the current chart state
   * This should be called whenever the dataset visibility is changed externally
   */
  updateStoredHiddenStates() {
    if (this.chartInstance) {
      this.originalHiddenStates = this.chartInstance.data.datasets.slice(0, this.originalBarDatasetCount).map(ds => ds.hidden);
    }
  }

  /**
   * Creates a debounced function that delays invoking the provided function
   * until after 'wait' milliseconds have elapsed since the last invocation.
   *
   * @param {Function} func - The function to debounce
   * @param {number} wait - The number of milliseconds to delay
   * @returns {Function} The debounced function
   */
  #debounce(func, wait) {
    let timeout;
    const controllerInstance = this;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func.apply(controllerInstance, args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
  }

  /**
   * Handles chart dataset hover logic to display statistical bounds and outlier detection
   * Uses pre-computed outlier analysis from Ruby instead of calculating statistics
   * When hovering over a dataset in the legend:
   * 1. Hides all original datasets
   * 2. Uses pre-computed bounds and outlier data from Ruby
   * 3. Highlights outlier points in red
   * 4. Shows statistical bounds and the hovered dataset line
   * 5. Hides irrelevant Y-axis (show only yConversions for conversions, ySpend for spend)
   *
   * @param {Event} evt - The hover event
   * @param {Object} item - The legend item being hovered
   * @param {Object} legend - The chart legend object
   */
  #doHoverLogic = (evt, item, legend) => {
      const chart = this.chartInstance;
      if (!chart) return;
      const hoveredDatasetIndex = item.datasetIndex;
      if (hoveredDatasetIndex >= this.originalBarDatasetCount) return;
      this.currentlyHoveredIndex = hoveredDatasetIndex;

      // Get pre-computed analysis for this dataset
      const analysis = this.outlierAnalysis[hoveredDatasetIndex];
      if (!analysis) return;

      // Determine if this is a spend dataset or conversion dataset
      const isSpendDataset = analysis.label === "Spend";

      // Slice data to show only the most recent points (displayDuration)
      const displayData = analysis.data.slice(-this.displayDuration);
      const lowerBoundData = analysis.lower_bounds.slice(-this.displayDuration);
      const upperBoundData = analysis.upper_bounds.slice(-this.displayDuration);
      const pointRadii = analysis.point_radii.slice(-this.displayDuration);
      const pointColors = analysis.point_colors.slice(-this.displayDuration);

      // Update chart datasets to show statistical bounds and hovered line
      chart.data.datasets.forEach((dataset, index) => {
          if (index < this.originalBarDatasetCount) {
              dataset.hidden = true;
          } else if (index === this.lowerBoundIndex) {
              dataset.label = 'Lower Bound (Q1 - 1.5×IQR)';
              dataset.data = lowerBoundData;
              dataset.hidden = false;
              dataset.order = 0;
              // Set the correct yAxisID based on dataset type
              dataset.yAxisID = isSpendDataset ? 'ySpend' : 'yConversions';
          } else if (index === this.upperBoundIndex) {
               dataset.label = 'Upper Bound (Q3 + 1.5×IQR)';
               dataset.data = upperBoundData;
               dataset.hidden = false;
               dataset.order = 0;
               // Set the correct yAxisID based on dataset type
               dataset.yAxisID = isSpendDataset ? 'ySpend' : 'yConversions';
          } else if (index === this.hoverLineIndex) {
              dataset.data = displayData;
              dataset.borderColor = analysis.border_color;
              dataset.borderWidth = (this.originalDatasetsInfo[hoveredDatasetIndex]?.borderWidth || 1) + 1;
              dataset.pointRadius = pointRadii;
              dataset.pointBackgroundColor = pointColors;
              dataset.hidden = false;
              dataset.order = 1;
              // Set the correct yAxisID based on dataset type
              dataset.yAxisID = isSpendDataset ? 'ySpend' : 'yConversions';
          }
      });

      // Hide/show appropriate Y-axes based on dataset type
      // Always hide yPercent during IQR hover — coverage lines are hidden and the axis is irrelevant
      if (chart.options.scales.yPercent) chart.options.scales.yPercent.display = false;
      if (isSpendDataset) {
          // Show only spend axis, hide conversions axis
          if (chart.options.scales.ySpend) chart.options.scales.ySpend.display = true;
          chart.options.scales.yConversions.display = false;
      } else {
          // Show only conversions axis, hide spend axis
          chart.options.scales.yConversions.display = true;
          if (chart.options.scales.ySpend) chart.options.scales.ySpend.display = false;
      }

      chart.config.data.labels = this.initialDataConfig.labels.slice(-this.displayDuration);
      chart.update('none');
  }

  /**
   * Handles mouse leave from chart legend items
   * Restores the chart to its original state by:
   * 1. Restoring the previous hidden/visible state of datasets
   * 2. Hiding statistical bounds and special datasets
   * 3. Restoring original chart appearance
   * 4. Restoring both Y-axes visibility
   *
   * @param {Event} evt - The leave event
   * @param {Object} item - The legend item being left
   * @param {Object} legend - The chart legend object
   */
  #doLeaveLogic = (evt, item, legend) => {
       const chart = this.chartInstance;
       if (!chart) return;
       this.currentlyHoveredIndex = null;
       chart.config.data.labels = this.initialDataConfig.labels.slice(-this.displayDuration);
       chart.data.datasets.forEach((dataset, index) => {
          if (index < this.originalBarDatasetCount) {
              dataset.data = this.initialDataConfig.datasets[index].data.slice(-this.displayDuration);
              // Restore the original hidden state
              dataset.hidden = this.originalHiddenStates[index] || false;
          } else if (index >= this.lowerBoundIndex) {
              dataset.hidden = true;
              dataset.data = [];
              if (index === this.hoverLineIndex) {
                   dataset.pointRadius = 3;
                   dataset.pointBackgroundColor = 'grey';
              }
          }
      });

      // Restore axis visibility based on visible datasets
      let hasVisibleConversions = false;
      let hasVisibleSpend = false;
      let hasVisiblePercent = false;

      for (let i = 0; i < this.originalBarDatasetCount; i++) {
          const dataset = chart.data.datasets[i];
          if (!dataset.hidden) {
              if (dataset.yAxisID === "ySpend") {
                  hasVisibleSpend = true;
              } else if (dataset.yAxisID === "yPercent") {
                  hasVisiblePercent = true;
              } else {
                  hasVisibleConversions = true;
              }
          }
      }

      chart.options.scales.yConversions.display = hasVisibleConversions;
      if (chart.options.scales.ySpend) chart.options.scales.ySpend.display = hasVisibleSpend;
      if (chart.options.scales.yPercent) chart.options.scales.yPercent.display = hasVisiblePercent;

      chart.update('none');
  }

  // --- Stimulus Lifecycle Methods ---

  /**
   * Initializes the chart when the controller connects to the DOM
   * This method:
   * 1. Creates the canvas element
   * 2. Loads and validates chart data
   * 3. Sets up statistical datasets for outlier detection
   * 4. Configures the chart with proper options
   * 5. Initializes hover/leave handlers for interactive features
   */
  connect() {
    const canvas = document.createElement("canvas");
    canvas.style.width = "100%";
    canvas.className = "chart-canvas";

    let values;
    try {
        values = JSON.parse(this.element.dataset.chartValues);
        if (!values || typeof values !== 'object') { throw new Error("Chart data is missing or invalid"); }
    } catch (e) {
        console.error("Error parsing chart data:", e); this.element.innerHTML = "Error loading chart data."; return;
    }

    values.data = values.data || { labels: [], datasets: [] };
    values.options = values.options || {};
    values.options.plugins = values.options.plugins || {};

    // Extract outlier analysis from Ruby
    this.outlierAnalysis = values.outlier_analysis || {};

    // Extract account and platform info for state persistence
    this.accountId = values.account_id;
    this.adAccountId = values.ad_account_id;
    this.platform = values.platform;

    // Allow the server to control the visible window. Falls back to the 30-day
    // default that other callers (admin dashboard, api_calls, lead_docket) rely on.
    if (Number.isFinite(values.display_duration) && values.display_duration > 0) {
      this.displayDuration = values.display_duration;
    }

    // Check if we're being used inside a wrapper component
    const isWrapped = this.element.closest('[data-controller*="iqr-chart-wrapper"]') !== null;

    // If the chart data opts into using its own provided scales (e.g. stacked bar charts),
    // skip the defensive x-axis removal so the scales pass through intact.
    const useProvidedScales = !!values.use_provided_scales;

    if (!useProvidedScales) {
      // *** DEFENSIVE CODING: Remove potential problematic x-axis config from input ***
      // This prevents errors when time-scale is specified but date adapter is missing
      if (values.options.scales?.x) {
          console.warn("Stimulus Chart Controller: Removing x-axis configuration from input data-chart-values. Using default category scale. Ensure labels are appropriate.");
          delete values.options.scales.x;
      }
      // *** Also remove time scale config if present at top level options (older Chart.js versions) ***
      if (values.options.scales?.xAxes) { // Handle older format just in case
          console.warn("Stimulus Chart Controller: Removing xAxes configuration from input data-chart-values.");
          delete values.options.scales.xAxes;
      }
    }

    // --- Store Full Data Length & Prepare Config ---
    this.fullDataLength = values.data.labels?.length || 0;
    // Ensure displayDuration is not longer than the actual data
    this.displayDuration = Math.min(this.displayDuration, this.fullDataLength);

    if (this.fullDataLength < this.displayDuration) {
        console.warn(`Input data length (${this.fullDataLength}) is less than desired display duration (${this.displayDuration}). Displaying all ${this.fullDataLength} points.`);
    }

    // Find the indices of statistical datasets (they come from Ruby now)
    let statisticalDatasetCount = 0;
    values.data.datasets.forEach((dataset, index) => {
        if (dataset.label && (
            dataset.label.includes('Lower Bound') ||
            dataset.label.includes('Upper Bound') ||
            dataset.label === 'Hovered Data'
        )) {
            if (dataset.label.includes('Lower Bound')) {
                this.lowerBoundIndex = index;
            } else if (dataset.label.includes('Upper Bound')) {
                this.upperBoundIndex = index;
            } else if (dataset.label === 'Hovered Data') {
                this.hoverLineIndex = index;
            }
            statisticalDatasetCount++;
        }
    });

    // Count of original bar datasets (excluding statistical datasets)
    this.originalBarDatasetCount = values.data.datasets.length - statisticalDatasetCount;

    // Store original styling information for datasets to restore after hover effects
    this.originalDatasetsInfo = values.data.datasets.slice(0, this.originalBarDatasetCount).map(ds => ({
        borderColor: ds.borderColor,
        borderWidth: ds.borderWidth
    }));

    // Store a deep copy of the original data for reference during interactions
    this.initialDataConfig = JSON.parse(JSON.stringify(values.data));

    // Slice data to show only the most recent points (displayDuration)
    const displayLabels = values.data.labels.slice(-this.displayDuration);
    const displayDatasets = values.data.datasets.map(ds => ({
        ...ds,
        data: Array.isArray(ds.data) ? ds.data.slice(-this.displayDuration) : []
    }));

    // Create debounced handlers for hover and leave events to improve performance
    this.debouncedHoverHandler = this.#debounce(this.#doHoverLogic, 75);
    this.debouncedLeaveHandler = this.#debounce(this.#doLeaveLogic, 75);

    // Custom legend generation function to only show original datasets and handle hover state
    const generateLabelsFunc = (chart) => {
        const originalGenerateLabels = Chart.defaults.plugins.legend.labels.generateLabels;
        const labels = originalGenerateLabels.call(this, chart);
        const filteredLabels = labels.filter(label => label.datasetIndex < this.originalBarDatasetCount);
        filteredLabels.forEach(label => {
            if (label.datasetIndex === this.currentlyHoveredIndex) { label.hidden = false; }
        });
        filteredLabels.sort((a, b) => a.datasetIndex - b.datasetIndex);
        return filteredLabels;
    };

    // --- Build Final Chart Config ---
    // Define scales for multiple Y-axes (conversions and spend).
    // When use_provided_scales is set, use the scales from the chart data instead.
    const finalScaleOptions = useProvidedScales
        ? (values.options.scales || {})
        : {
            yConversions: { title: { text: "Conversions", display: true }, position: "left" },
            ySpend: { title: { text: "Spend", display: true }, position: "right", grid: { display: false } }
            // No x-axis explicitly defined here
          };

    // Only add yPercent axis when there are datasets using it
    const hasPercentDatasets = displayDatasets.some(ds => ds.yAxisID === "yPercent");
    if (hasPercentDatasets) {
        finalScaleOptions.yPercent = {
            type: "linear",
            display: true,
            position: "right",
            beginAtZero: true,
            max: 100,
            title: { text: "Coverage %", display: true },
            ticks: { precision: 0, callback: (value) => value + "%" },
            grid: { drawOnChartArea: false }
        };
    }

    // Create complete chart configuration
    const config = {
        type: "bar",
        // ...values,
        data: { labels: displayLabels, datasets: displayDatasets },
        initialData: this.initialDataConfig, // Store full initial data
        options: {
            animation: false, responsive: true, maintainAspectRatio: false,
            ...values.options, // Spread other options from input values *first*
            interaction: { mode: 'index', intersect: false, ...(values.options.interaction || {}) },
            spanGaps: true,
            scales: finalScaleOptions, // Apply the cleaned, specific scales *last*
            plugins: {
                ...values.options.plugins,
                legend: {
                    display: !isWrapped, // Hide legend if wrapped
                    ...(values.options.plugins?.legend || {}),
                    onHover: this.debouncedHoverHandler,
                    onLeave: this.debouncedLeaveHandler,
                    onClick: (evt, item, legend) => {
                        // Prevent default click behavior when we're in hover state
                        if (this.currentlyHoveredIndex !== null) {
                            return false;
                        }
                        // Allow default behavior when not hovering
                        return true;
                    },
                    labels: {
                        ...(values.options.plugins?.legend?.labels || {}),
                        generateLabels: generateLabelsFunc
                    }
                },
                tooltip: { ...(values.options.plugins?.tooltip || {}) }
            }
        }
    };

    // --- Create Chart ---
    try {
        Chart.defaults.font.size = 14;
        this.chartInstance = new Chart(canvas, config);
        this.element.innerHTML = '';
        this.element.appendChild(canvas);
        // Set flag to indicate the chart is connected
        this.element.dataset.iqrChartConnected = 'true';

        // Initialize stored hidden states
        this.updateStoredHiddenStates();

    } catch(e) {
        console.error("Error creating chart:", e); this.element.innerHTML = "Error creating chart.";
    }
  }

  /**
   * Clean up the chart instance when the controller disconnects from the DOM
   * This prevents memory leaks by properly destroying the Chart.js instance
   */
  disconnect() {
    if (this.chartInstance) {
      this.chartInstance.destroy();
      this.chartInstance = null;
    }
  }
}
